import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject,
    List,
} from '@denishussar/html5-game-engine';
import { AnimationContents } from '../../../../AnimationContents';
import { GameLogic } from '../../../Logic/GameLogic';
import { GameModel } from '../../../Models/Game/GameModel';
import { GameController } from '../../Game/GameController';
import { MiniGameTypeEnum } from '../../../Models/Game/MiniGameModel';
import { MiniMapMiniGameController } from './MiniMapMiniGameController';
import { BonusTypeEnum } from '../../../Models/Game/BonusModel';
import { MiniMapBonusController } from './MiniMapBonusController';
import { MiniMapControllerButton } from './MiniMapControllerButton';
import { TaskType } from '../../../Models/Game/TaskModel';

export interface IMiniMapControllerOptions {
    gameController: GameController;
}

export class MiniMapController extends AnimationBaseController {
    $model: GameModel;

    private _width: number = 0;
    private _height: number = 0;

    private _screenWidth: number = 0;
    private _screenHeight: number = 0;
    private _screenScale: number = 1;

    private _miniGameControllerList: List<MiniMapMiniGameController> = new List<MiniMapMiniGameController>();
    private _bonusControllerList: List<MiniMapBonusController> = new List<MiniMapBonusController>();

    private _margin: [number, number] = [100, -100];

    public constructor(
        @Inject private _options: IMiniMapControllerOptions,
        @Inject private _gameLogic: GameLogic,
    ) {
        super('MiniMapController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return {
            animationContent: AnimationContents.MINIMAP_SCREEN_JSON,
            libraryName: 'mini_map_screen',
            childControllers: {
                button_close: {
                    instanceName: 'button_close',
                    view: MiniMapControllerButton,
                    model: this.$model,
                    options: {
                        onClick: () => {
                            this._gameLogic.resetGame();
                        },
                    },
                },
            },
            modelWatch: {
                pos: () => this.updatePos(),
            },
            modelEvents: {
                addTask: (miniGameType: MiniGameTypeEnum) => {
                    this.addMiniGame(miniGameType);
                    this.updateGlobal();
                },
                removeTask: (miniGameType: MiniGameTypeEnum) => {
                    const controller = this.$childControllers.get<MiniMapMiniGameController>(`minimap_${miniGameType}`);

                    if (controller) {
                        this._miniGameControllerList.remove(controller);
                        this.removeChildController(controller);
                        this.updateGlobal();
                    }
                },
                addBonus: (bonusType: BonusTypeEnum, roomIndex: number) => {
                    this.addBonus(bonusType, roomIndex);
                },
                removeBonus: (bonusType: BonusTypeEnum, roomIndex: number) => {
                    this.removeChildControllerByName(`minimap_${bonusType}_${roomIndex}`);
                },
            },
            queue: {},
        };
    }

    protected onInitialize() {
        this._width = this.display.width;
        this._height = this.display.height;

        this.gotoAndStopByQuery('unit', this.$model.isWoman ? 1 : 0);

        this._gameLogic.getActiveTasks().forEach(o => this.addMiniGame(o.miniGameType));
        this._gameLogic.getActiveBonuses().forEach(o => this.addBonus(o.type, o.roomIndex));

        this.updatePos();
        this.updateGlobal();
    }

    protected onResize(width: number, height: number, scale: number) {
        this._screenWidth = width;
        this._screenHeight = height;
        this._screenScale = scale;

        this.updateScale();
    }

    protected onLandscape(): void {
        this.updateScale();
    }

    protected onPortrait(): void {
        this.updateScale();
    }

    private updatePos(): void {
        const unit = this.$model.unitList.elementAt(0);
        const unitPos = unit.pos;

        this.setPosByQuery('unit', unitPos[0] - this.width / 2 + this._margin[0], unitPos[1] - this.height / 2 + this._margin[1]);
    }

    private updateScale(): void {
        this.display.scale.set(Math.min(this._screenWidth / this._width / this._screenScale, this._screenHeight / this._height / this._screenScale) * 0.975);
    }

    private addMiniGame(miniGameType: MiniGameTypeEnum): void {
        const miniGame = this._gameLogic.getMiniGameByType(miniGameType);
        const task = this._gameLogic.getTaskByMiniGameType(miniGameType);

        if (miniGame && task) {
            const controller = this.addChildController({
                view: MiniMapMiniGameController,
                model: task,
            }, `minimap_${miniGameType}`, 3) as MiniMapMiniGameController;

            controller.x = miniGame.pos[0] - this.width / 2 + this._margin[0];
            controller.y = miniGame.pos[1] - this.height / 2 + this._margin[1];

            this._miniGameControllerList.add(controller);
        }
    }

    private addBonus(bonusType: BonusTypeEnum, roomIndex: number): void {
        const bonus = this._gameLogic.getBonusByTypeAndRoom(bonusType, roomIndex);

        if (bonus) {
            const controller = this.addChildController({
                view: MiniMapBonusController,
                model: bonus,
            }, `minimap_${bonusType}_${roomIndex}`, 3) as MiniMapBonusController;

            controller.x = bonus.pos[0] - this.width / 2 + this._margin[0];
            controller.y = bonus.pos[1] - this.height / 2 + this._margin[1];

            this._bonusControllerList.add(controller);
        }
    }

    private updateGlobal(): void {
        if (this._gameLogic.getGlobalTask()) {
            this._bonusControllerList.forEach(o => o.alpha = 0.35);
            this._miniGameControllerList.forEach(o => o.alpha = o.$model.type === TaskType.Global ? 1 : 0.25);

        } else {
            this._bonusControllerList.forEach(o => o.alpha = 1);
            this._miniGameControllerList.forEach(o => o.alpha = 1);
        }
    }
}
